import { useEffect, useRef } from 'react'

/** Пауза в наборе, после которой поле сохраняется само (та же, что у блоков). */
export const SAVE_AFTER_PAUSE_MS = 700

/**
 * Сохранение текстового поля после паузы в наборе. Только потеря фокуса не годится: при
 * обновлении страницы `blur` не срабатывает, и правка терялась без единой ошибки. `save`
 * сам отбрасывает неизменённое значение, поэтому первый рендер запроса не шлёт.
 *
 * Уход раньше паузы: размонтирование (смена маршрута) досылает ждущую правку, а закрытие
 * вкладки просит подтверждение — запрос из `beforeunload` браузер не гарантирует.
 */
export const useSaveAfterPause = (value: unknown, save: () => unknown, enabled = true): void => {
  const saveRef = useRef(save)
  saveRef.current = save
  const pendingRef = useRef(false)
  const mountedRef = useRef(false)

  useEffect(() => {
    // Первый рендер: значение не менялось, ждать нечего.
    if (!mountedRef.current) {
      mountedRef.current = true
      return
    }
    if (!enabled) {
      // Поле заблокировано или опустело: ждущую правку не досылаем.
      pendingRef.current = false
      return
    }
    pendingRef.current = true
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    const timer = window.setTimeout(() => {
      pendingRef.current = false
      window.removeEventListener('beforeunload', onBeforeUnload)
      saveRef.current()
    }, SAVE_AFTER_PAUSE_MS)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [value, enabled])

  useEffect(
    () => () => {
      if (pendingRef.current) saveRef.current()
    },
    [],
  )
}
